import React, { useEffect, useRef, useState } from 'react';

// @ts-ignore
import styles from './LandingPage.module.scss?module';
import { Col, Container, Image, Row } from 'react-bootstrap';
import useInView from '../../../Hooks/useInView';
import { useNcw } from '../Context/NcwContext';
import { formatDateTime2digit } from '../../../Helper/formatter';
import { useNavigate } from 'react-router-dom';
import AddToCalendar from './AddToCalendar/AddToCalendar';

// NCW banners are hosted as Public Assets (nas-erp, P-4856): stable public
// URLs on the docs-pub-01 bucket, swappable by Marketing without a deploy —
// and every view feeds the use-it-or-lose-it access tracking.
const bannerDesktop =
  'https://fra1.digitaloceanspaces.com/docs-pub-01/public_assets/naskorsports/ncw/ncw-0926-banner-desktop.jpg';
const bannerMobile =
  'https://fra1.digitaloceanspaces.com/docs-pub-01/public_assets/naskorsports/ncw/ncw-0926-banner-mobile.jpg';

export default function LandingPage() {
  const { ncw } = useNcw();
  const [isPageImgAndDateInView, setIsPageImgAndDateInView] = useState(false);
  const imgCardParentRef = useRef<HTMLDivElement>(null);
  const titleAndDescRef = useRef(null);
  const ExploreSectionRef = useRef(null);
  const isTitleAndDescInView = useInView(titleAndDescRef);
  const isExploreSectionInView = useInView(ExploreSectionRef);
  const isImgCardsInView = useInView(imgCardParentRef);
  const [ncwDate, setNcwDate] = useState({ start: '', end: '' });
  const navigate = useNavigate();

  useEffect(() => {
    if (isPageImgAndDateInView == false) {
      setTimeout(() => {
        setIsPageImgAndDateInView(true);
      }, 100);
    }
  }, [isPageImgAndDateInView]);

  useEffect(() => {
    if (ncw && ncw.start && ncw.end) {
      if (new Date() >= new Date(ncw.start) && new Date() < new Date(ncw.end)) {
        navigate('home');
        return;
      } else if (new Date() > new Date(ncw.end)) {
        navigate('/');
        return;
      }

      setNcwDate({ start: ncw.start, end: ncw.end });
    }
  }, [ncw]);

  return (
    <>
      <Container className="d-flex gap-4 gap-lg-5 flex-column">
        <Row>
          <Col
            className={
              isPageImgAndDateInView
                ? styles.VisiblePageImg
                : styles.HiddenPageImg
            }
          >
            <Image
              className={'w-100 d-none d-md-block'}
              src={bannerDesktop}
              alt="Newcomer Week - save the date"
            />
            <Image
              className={'w-100 d-md-none'}
              src={bannerMobile}
              alt="Newcomer Week - save the date"
            />
          </Col>
        </Row>
        <div
          className="d-flex gap-4 gap-lg-5 flex-column overflow-hidden"
          ref={titleAndDescRef}
        >
          <Row>
            <Col
              className={
                isTitleAndDescInView
                  ? styles.VisiblePageTitle
                  : styles.HiddenPageTitle
              }
            >
              <h1>Find Your Perfect Matches</h1>
            </Col>
          </Row>
          <Row>
            <Col
              className={`${
                isTitleAndDescInView
                  ? styles.VisiblePageDesc
                  : styles.HiddenPageDesc
              }`}
            >
              <p className="m-0">
                From {!!ncwDate.start && formatDateTime2digit(ncwDate.start)} to
                {!!ncwDate.end && ` ${formatDateTime2digit(ncwDate.end)}`}, it’s
                all about the newcomers. During Newcomer Week, we put our newly
                listed brands and products center stage – supported by exclusive
                limited-time discounts and a unique way of showcasing and
                shopping..
              </p>
              <br />
              <p>This is your chance to:</p>
              <ul>
                <li>Discover exciting newcomers.</li>
                <li>
                  Learn more about the newest brands and products in our
                  portfolio.
                </li>
                <li>
                  Find products with the potential to inspire your customers and
                  grow your business.
                </li>
                <li>Enjoy special offers available only during this week.</li>
              </ul>
              <p>
                Whether you’re searching for the next best-seller or fresh
                inspiration, Newcomer Week makes it easy to find the perfect
                match for you and your customers.
              </p>
              <br />
              <p className="m-0">
                Save the date – add the event to your calendar now and be part
                of a week full of innovation, inspiration, and opportunities.
              </p>
            </Col>
          </Row>
          <Row>
            <Col
              className={`text-center ${
                isTitleAndDescInView
                  ? styles.VisibleCalendarBtn
                  : styles.HiddenCalendarBtn
              }`}
            >
              <AddToCalendar start={ncwDate.start} end={ncwDate.end} />
            </Col>
          </Row>
        </div>
        <Row aria-label="spacer" />
        <Row ref={imgCardParentRef}>
          {AnimatedCards({ isImgCardsInView: isImgCardsInView })}
        </Row>
        <Row aria-label="spacer" />
        <Row
          className="gap-4 gap-md-0"
          style={{ contain: 'paint' }}
          ref={ExploreSectionRef}
        >
          <Col
            xs={12}
            md={6}
            className={`d-flex align-items-center flex-column gap-4 justify-content-center ${
              isExploreSectionInView
                ? styles.VisibleExploreDesc
                : styles.HiddenExploreDesc
            }`}
          >
            <p className="m-0">
              Explore new Brands and Products During our exclusive Newcomer
              Week, you’ll benefit from special offers and get the chance to
              explore the newest brands and products in our portfolio – their
              potential, their values, and what makes them unique. Find the
              perfect match for you and your customers. Click on the button and
              save the event directly in your calendar
            </p>
            <AddToCalendar start={ncwDate.start} end={ncwDate.end} />
          </Col>
          <Col
            xs={12}
            md={6}
            className={`${
              isExploreSectionInView
                ? styles.VisibleExploreImg
                : styles.HiddenExploreImg
            }`}
          >
            <img
              className="w-100"
              src="https://files.naskorsports.com/shop/newcomer-week/landing-page/newcomer-week-landingpage-LP-pre-Event.jpg"
              alt="LP pre Event image"
            />
          </Col>
        </Row>
        <Row aria-label="spacer" />
      </Container>
    </>
  );
}

const ANIMATION_SPEED = [
  {
    angleSpeed: '5',
    imgSrc:
      'https://files.naskorsports.com/shop/newcomer-week/landing-page/special-promotions.png',
  },
  {
    angleSpeed: '3.5',
    imgSrc:
      'https://files.naskorsports.com/shop/newcomer-week/landing-page/sample-box.png',
  },
  {
    angleSpeed: '2',
    imgSrc:
      'https://files.naskorsports.com/shop/newcomer-week/landing-page/amazing-brands.png',
  },
] as const;

const AnimatedCards = ({ isImgCardsInView }: { isImgCardsInView: boolean }) => {
  return ANIMATION_SPEED.map((element, index) => {
    return (
      <Col
        className={`${styles.ImgCardsParent} ${
          isImgCardsInView ? styles.VisibleImgCards : styles.HiddenImgCards
        }`}
        xs={4}
        key={index}
      >
        <div
          // className={`${styles.AnimationConicBorder}`}
          data-speed={element.angleSpeed}
        >
          <Image
            style={{ borderRadius: '5px' }}
            src={element.imgSrc}
            className={'w-100'}
          />
        </div>
      </Col>
    );
  });
};
